import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import { PLAN_LIMITS, PLAN_FEATURES } from "../services/plans";
import ChannelPicker from "../components/ChannelPicker";
import PlanCards from "../components/PlanCards";
import "./Onboarding.css";

const PLATFORM_CATALOG = [
  { platform: "telegram", icon: "\u2708", color: "#0088cc", comingSoon: false },
  { platform: "whatsapp", icon: "\uD83D\uDCAC", color: "#25D366", comingSoon: true },
];

export default function Onboarding() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pickerRef = useRef();

  // Step from URL
  const rawStep = parseInt(searchParams.get("step")) || 1;
  const step = Math.min(Math.max(rawStep, 1), 4);
  const setStep = (s) => setSearchParams({ step: s }, { replace: false });

  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscribing, setSubscribing] = useState(false);
  const [channelsAdded, setChannelsAdded] = useState(0);

  // Checkout state
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Platform state
  const [platformLinked, setPlatformLinked] = useState(false);
  const [platformLinkUrl, setPlatformLinkUrl] = useState(null);
  const [platformConnecting, setPlatformConnecting] = useState(false);
  const [platformPolling, setPlatformPolling] = useState(false);

  const limit = PLAN_LIMITS[user?.plan] ?? 0;
  const presetLimit = PLAN_FEATURES[user?.plan]?.presets ?? 1;
  const currentCount = subscriptions.length;

  // Fetch subscriptions on mount
  const fetchSubs = useCallback(async () => {
    try {
      setSubscriptions(await api.listSubscriptions());
    } catch {}
  }, []);

  useEffect(() => {
    fetchSubs();
  }, [fetchSubs]);

  // Check platform status on mount
  useEffect(() => {
    api.getPlatformStatus("telegram")
      .then((r) => setPlatformLinked(r.linked))
      .catch(() => {});
  }, []);

  // Platform polling effect
  useEffect(() => {
    if (!platformPolling) return;
    const interval = setInterval(async () => {
      try {
        const { linked } = await api.getPlatformStatus("telegram");
        if (linked) {
          setPlatformLinked(true);
          setPlatformPolling(false);
          setPlatformConnecting(false);
          setPlatformLinkUrl(null);
        }
      } catch {}
    }, 3000);
    const timeout = setTimeout(() => {
      setPlatformPolling(false);
      setPlatformConnecting(false);
      setPlatformLinkUrl(null);
    }, 600000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [platformPolling]);

  const handleSelectPlan = async (plan) => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      localStorage.setItem("viewsday_onboarding_in_progress", "true");
      const { checkout_url } = await api.createCheckout(plan);
      if (checkout_url.startsWith("http") && !checkout_url.includes(window.location.host)) {
        window.location.href = checkout_url;
      } else {
        // Internal redirect (e.g., existing subscriber upgrade)
        const path = checkout_url.startsWith("http")
          ? new URL(checkout_url).pathname + new URL(checkout_url).search
          : checkout_url;
        navigate(path);
      }
    } catch (err) {
      setCheckoutLoading(false);
      if (err.status === 400) {
        setCheckoutError(err.message);
      } else {
        setCheckoutError(t("subscription.errorGeneric"));
      }
    }
  };

  const handleUpgradeFromPicker = () => {
    const nextPlan = user?.plan === "free" ? "starter" : "pro";
    handleSelectPlan(nextPlan);
  };

  const handleNext = async () => {
    if (step === 2) {
      const { selectedPresetIds, selectedChannelIds, manualUrls, categories } = pickerRef.current.getSelection();
      const urls = [...selectedChannelIds, ...manualUrls];
      setSubscribing(true);
      try {
        for (const presetId of selectedPresetIds) {
          await api.subscribePreset(presetId);
        }
        if (urls.length > 0) {
          const res = await api.subscribe(urls, categories);
          setChannelsAdded(res.subscribed?.length || 0);
        }
        setChannelsAdded((prev) => prev + selectedPresetIds.length);
      } catch {}
      setSubscribing(false);
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const handleConnectTelegram = async () => {
    setPlatformConnecting(true);
    try {
      const { authorize_url } = await api.authorizePlatform("telegram");
      setPlatformLinkUrl(authorize_url);
      setPlatformPolling(true);
    } catch {
      setPlatformConnecting(false);
    }
  };

  const handleFinish = () => {
    localStorage.removeItem("viewsday_onboarding_in_progress");
    api.completeOnboarding().catch(() => {});
    navigate("/dashboard");
  };

  return (
    <div className="onboarding">
      <div className="ob-container">
        <img
          src="/logos/mark-black-background.jpg"
          alt="ViewsDay"
          className="ob-logo"
        />

        {/* Step indicator */}
        <div className="ob-steps">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`ob-dot${s === step ? " ob-dot--active" : ""}${s < step ? " ob-dot--done" : ""}`}
            />
          ))}
          <span className="ob-step-label">
            {t("onboarding.stepOf", { step, total: 4 })}
          </span>
        </div>

        <h1 className="ob-title">{t(`onboarding.step${step}Title`)}</h1>
        <p className="ob-desc">{t(`onboarding.step${step}Desc`)}</p>

        <div className="ob-content">
          {/* Step 1: Choose Plan */}
          {step === 1 && (
            <div className="ob-plans">
              <PlanCards
                currentPlan={user?.plan || "free"}
                onSelectPlan={handleSelectPlan}
                loading={checkoutLoading}
                loadingPlan={checkoutLoading ? "loading" : null}
              />
              {checkoutError && (
                <div className="ob-checkout-error">{checkoutError}</div>
              )}
            </div>
          )}

          {/* Step 2: Choose Channels */}
          {step === 2 && (
            <ChannelPicker
              ref={pickerRef}
              planPresetLimit={presetLimit}
              planChannelLimit={limit}
              currentPresetCount={0}
              currentChannelCount={currentCount}
              showUrlInput
              onUpgrade={handleUpgradeFromPicker}
            />
          )}

          {/* Step 3: Connect Telegram */}
          {step === 3 && (
            <div className="ob-platforms">
              {PLATFORM_CATALOG.map((cat) => {
                const isTelegram = cat.platform === "telegram";
                const isConnected = isTelegram && platformLinked;

                return (
                  <div
                    key={cat.platform}
                    className={`ob-platform-card${isConnected ? " ob-platform-connected" : ""}${cat.comingSoon ? " ob-platform-disabled" : ""}`}
                    style={{ "--platform-color": cat.color }}
                  >
                    <div className="ob-platform-header">
                      <div
                        className="ob-platform-icon"
                        style={{ background: cat.color }}
                      >
                        {cat.icon}
                      </div>
                      <div className="ob-platform-name">
                        {t(`onboarding.${cat.platform}Card`)}
                      </div>
                      {isConnected && (
                        <span className="ob-platform-status">
                          {t("dashboard.connected")}
                        </span>
                      )}
                      {cat.comingSoon && (
                        <span className="ob-platform-coming-soon">
                          {t("dashboard.comingSoon")}
                        </span>
                      )}
                    </div>

                    <p className="ob-platform-desc">
                      {t(`dashboard.${cat.platform}Desc`)}
                    </p>

                    {isTelegram && !cat.comingSoon && (
                      <div className="ob-platform-action">
                        {isConnected ? (
                          <div className="ob-connected-msg">
                            {t("onboarding.telegramConnected")}
                          </div>
                        ) : !platformConnecting ? (
                          <button
                            className="btn-primary btn-sm"
                            onClick={handleConnectTelegram}
                          >
                            {t("onboarding.connectTelegram")}
                          </button>
                        ) : (
                          <div className="ob-link-section">
                            <p className="ob-link-instructions">
                              {t("dashboard.telegramLinkReady")}
                            </p>
                            {platformLinkUrl && (
                              <a
                                href={platformLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-platform"
                                style={{ background: cat.color }}
                              >
                                {t("onboarding.openTelegram")} ↗
                              </a>
                            )}
                            {platformPolling && (
                              <div className="ob-polling">
                                <div
                                  className="polling-dot"
                                  style={{ background: cat.color }}
                                />
                                <span>{t("onboarding.waitingForLink")}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 4 && (
            <div className="ob-summary">
              <div className="ob-summary-items">
                <div className="ob-summary-item">
                  <span className="ob-summary-check">&#10003;</span>
                  <span>{t("onboarding.summaryChannels", { count: channelsAdded })}</span>
                </div>
                <div className={`ob-summary-item${platformLinked ? "" : " ob-summary-item--inactive"}`}>
                  <span className="ob-summary-check">
                    {platformLinked ? "\u2713" : "\u2013"}
                  </span>
                  <span>
                    {platformLinked
                      ? t("onboarding.summaryTelegram")
                      : t("onboarding.summaryNoTelegram")}
                  </span>
                </div>
              </div>

              <div className="ob-delivery-reminder">
                <span className="ob-delivery-icon">&#9200;</span>
                <span>
                  {t("onboarding.deliveryReminder", {
                    time: user?.delivery_time?.slice(0, 5) || "07:00",
                  })}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="ob-footer">
          {step === 1 ? (
            <>
              <div />
              <button className="btn-ghost btn-md ob-skip-plan" onClick={() => setStep(2)}>
                {t("subscription.continueWithCurrentPlan")}
              </button>
            </>
          ) : step < 4 ? (
            <>
              <button className="ob-skip" onClick={() => setStep(step + 1)}>
                {step === 3 ? t("onboarding.doLater") : t("onboarding.skip")}
              </button>
              <button
                className="btn-primary btn-md ob-next"
                onClick={handleNext}
                disabled={subscribing}
              >
                {subscribing ? (
                  <span className="btn-spinner" />
                ) : (
                  t("onboarding.next")
                )}
              </button>
            </>
          ) : (
            <>
              <div />
              <button
                className="btn-primary btn-md ob-next"
                onClick={handleFinish}
              >
                {t("onboarding.finish")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
